import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import type { IWhatsAppLinkRepository, RepositoryError } from '@application/ports';
import type { WhatsAppLinkCode, WhatsAppLinkStatus } from '@domain/entities/WhatsAppLink';
import { firebase } from '../firebase/client';
import { translateCallableError } from './support';

/**
 * El vínculo de WhatsApp contra el servidor: tres callables y ninguna lectura
 * directa. Las colecciones del asistente no tienen regla en `firestore.rules`,
 * igual que `orgCharts`, así que ningún cliente llega a un número de teléfono
 * por su cuenta.
 */

interface StatusPayload {
  readonly linked: unknown;
  readonly numberTail: unknown;
  readonly linkedAtMs: unknown;
}

interface CodePayload {
  readonly code: unknown;
  readonly expiresAtMs: unknown;
  readonly botNumber: unknown;
  readonly deepLink: unknown;
}

const textOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const malformed = (what: string): RepositoryError => ({
  kind: 'malformed-document',
  path: what,
  detail: 'El servidor contestó algo que no se pudo leer.',
});

export class FirestoreWhatsAppLinkRepository implements IWhatsAppLinkRepository {
  async status(): Promise<Result<WhatsAppLinkStatus, RepositoryError>> {
    try {
      const { functions } = firebase();
      const { data } = await httpsCallable<void, StatusPayload>(functions, 'getWhatsAppLink')();
      return ok({
        linked: data.linked === true,
        numberTail: textOrNull(data.numberTail),
        linkedAt: typeof data.linkedAtMs === 'number' && data.linkedAtMs > 0 ? new Date(data.linkedAtMs) : null,
      });
    } catch (cause) {
      return err(translateCallableError(cause, 'getWhatsAppLink'));
    }
  }

  async start(): Promise<Result<WhatsAppLinkCode, RepositoryError>> {
    try {
      const { functions } = firebase();
      const { data } = await httpsCallable<void, CodePayload>(functions, 'startWhatsAppLink')();
      const code = textOrNull(data.code);
      if (code === null || typeof data.expiresAtMs !== 'number') return err(malformed('startWhatsAppLink'));
      return ok({
        code,
        expiresAt: new Date(data.expiresAtMs),
        botNumber: textOrNull(data.botNumber),
        deepLink: textOrNull(data.deepLink),
      });
    } catch (cause) {
      return err(translateCallableError(cause, 'startWhatsAppLink'));
    }
  }

  async unlink(): Promise<Result<void, RepositoryError>> {
    try {
      const { functions } = firebase();
      await httpsCallable<void, unknown>(functions, 'unlinkWhatsApp')();
      return ok(undefined);
    } catch (cause) {
      return err(translateCallableError(cause, 'unlinkWhatsApp'));
    }
  }
}
